import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../config/ApiClient';
import { showError } from '../components/Alert';
import ProductList from '../components/ProductList';
import FilterSidebar, { Filters } from '../components/FilterSideBar';
import Pagination from '../components/Pagination';
import '../styles/ProductsPage.css';
import type { ItemDTO } from '../dto/item/ItemDTO';
import {
  fetchPublicItemsV2,
  type PublicItemsV2Params,
} from '../services/items';

type CacheEntry = {
  items: ItemDTO[];
  total?: number;
  pageSize?: number;
};

const ProductsPage: React.FC = () => {
  const location = useLocation();
  const [products, setProducts] = useState<ItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    category: [],
    color: [],
    size: [],
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  const [sortOrder, setSortOrder] = useState('featured');

  const perPage = 12;

  // simple in-memory cache to avoid refetching the same keyword/page combo
  const cacheRef = useRef<Record<string, CacheEntry>>({});
  const prevBaseKeyRef = useRef<string>('');
  const [feedMeta, setFeedMeta] = useState({ total: 0, pageSize: perPage });

  // handle window resize for mobile sidebar button
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // build a single keyword string from all selected filters
  const buildKeyword = () =>
    [...filters.category, ...filters.color, ...filters.size]
      .filter(Boolean)
      .join(' ');

  const params = new URLSearchParams(location.search);
  const keywordParam = params.get('keyword')?.trim() || '';
  const genderParamRaw = params.get('gender')?.trim().toLowerCase() || '';
  const genderParam =
    genderParamRaw === 'men' || genderParamRaw === 'women'
      ? genderParamRaw
      : '';

  const categories = useMemo(
    () => filters.category.map((c) => c.trim().toLowerCase()).filter(Boolean),
    [filters.category]
  );
  const colors = useMemo(
    () => filters.color.map((c) => c.trim().toLowerCase()).filter(Boolean),
    [filters.color]
  );
  const sizes = useMemo(
    () => filters.size.map((s) => s.trim()).filter(Boolean),
    [filters.size]
  );

  const categoriesKey = categories.join(',');
  const colorsKey = colors.join(',');
  const sizesKey = sizes.join(',');

  const filterKeyword = buildKeyword();
  const shouldUseV2 = Boolean(genderParam);

  const baseKeyParts: string[] = [];
  if (genderParam) baseKeyParts.push(`gender:${genderParam}`);
  if (keywordParam) baseKeyParts.push(`keyword:${keywordParam.toLowerCase()}`);
  if (categoriesKey) baseKeyParts.push(`categories:${categoriesKey}`);
  if (colorsKey) baseKeyParts.push(`colors:${colorsKey}`);
  if (sizesKey) baseKeyParts.push(`sizes:${sizesKey}`);
  if (!shouldUseV2 && filterKeyword)
    baseKeyParts.push(`filter:${filterKeyword.toLowerCase()}`);

  const baseKey = baseKeyParts.join('|') || '__all__';
  const cacheKey = shouldUseV2 ? `${baseKey}|page:${currentPage}` : baseKey;
  const keywordForLegacy = [keywordParam, filterKeyword]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (!prevBaseKeyRef.current) {
      prevBaseKeyRef.current = baseKey;
      return;
    }

    if (prevBaseKeyRef.current !== baseKey) {
      setFeedMeta({ total: 0, pageSize: perPage });
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  }, [baseKey, currentPage, perPage]);

  // fetch products whenever URL keyword, filters, or active page changes
  useEffect(() => {
    if (
      prevBaseKeyRef.current &&
      prevBaseKeyRef.current !== baseKey &&
      currentPage !== 1
    ) {
      return;
    }

    const cached = cacheRef.current[cacheKey];
    if (cached) {
      setProducts(cached.items);
      if (shouldUseV2) {
        const resolvedPageSize =
          cached.pageSize && cached.pageSize > 0 ? cached.pageSize : perPage;
        const resolvedTotal =
          cached.total ??
          (currentPage - 1) * resolvedPageSize + cached.items.length;
        setFeedMeta({
          total: Math.max(
            resolvedTotal,
            (currentPage - 1) * resolvedPageSize + cached.items.length
          ),
          pageSize: resolvedPageSize,
        });
      } else {
        setFeedMeta({ total: cached.items.length, pageSize: perPage });
      }
      setLoading(false);
      prevBaseKeyRef.current = baseKey;
      return;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        let entry: CacheEntry;

        if (shouldUseV2) {
          const payload: PublicItemsV2Params = {
            gender: genderParam,
            page: currentPage,
          };
          if (categories.length) payload.category = categories[0];
          if (colors.length) payload.colors = colors;
          if (sizes.length) payload.sizes = sizes;

          const res = await fetchPublicItemsV2(payload);
          const items = res.data.items ?? [];
          const responsePageSize =
            res.data.pageSize && res.data.pageSize > 0
              ? res.data.pageSize
              : perPage;
          const responsePage = res.data.page ?? currentPage;
          let resolvedTotal = res.data.total ?? null;
          if (resolvedTotal == null) {
            const baseTotal =
              Math.max(responsePage - 1, 0) * responsePageSize + items.length;
            resolvedTotal = res.data.hasMore
              ? baseTotal + responsePageSize
              : baseTotal;
          }

          entry = {
            items,
            total: resolvedTotal ?? items.length,
            pageSize: responsePageSize,
          };
        } else {
          const request = keywordForLegacy
            ? apiClient.get<ItemDTO[]>('/item/public/search', {
                params: { keyword: keywordForLegacy },
              })
            : apiClient.get<ItemDTO[]>('/item/public');
          const res = await request;
          entry = {
            items: res.data,
            total: res.data.length,
            pageSize: perPage,
          };
        }

        if (cancelled) return;

        cacheRef.current[cacheKey] = entry;
        setProducts(entry.items);
        if (shouldUseV2) {
          const resolvedPageSize =
            entry.pageSize && entry.pageSize > 0 ? entry.pageSize : perPage;
          const resolvedTotal =
            entry.total ??
            (currentPage - 1) * resolvedPageSize + entry.items.length;
          setFeedMeta({
            total: Math.max(
              resolvedTotal,
              (currentPage - 1) * resolvedPageSize + entry.items.length
            ),
            pageSize: resolvedPageSize,
          });
        } else {
          setFeedMeta({ total: entry.items.length, pageSize: perPage });
        }

        prevBaseKeyRef.current = baseKey;
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        showError('Failed to load products.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    baseKey,
    cacheKey,
    shouldUseV2,
    currentPage,
    genderParam,
    categories,
    categoriesKey,
    colors,
    colorsKey,
    sizes,
    sizesKey,
    keywordForLegacy,
    perPage,
  ]);

  // toggle any checkbox filter
  const onFilterChange = (filterType: keyof Filters, value: string) => {
    setFilters((f) => {
      const arr = f[filterType];
      const next = arr.includes(value)
        ? arr.filter((x) => x !== value)
        : [...arr, value];
      return { ...f, [filterType]: next };
    });
    setCurrentPage(1);
  };

  // clear all filters
  const resetFilters = () => {
    setFilters({ category: [], color: [], size: [] });
    setCurrentPage(1);
  };

  // client‐side sort (keeps your HTML; mutates list in place for now)
  const onSort = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSortOrder(val);
    let sorted = [...products];
    if (val === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    if (val === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    setProducts(sorted);
  };

  const start = (currentPage - 1) * perPage;
  const effectivePageSize =
    shouldUseV2 && feedMeta.pageSize > 0 ? feedMeta.pageSize : perPage;
  const displayedProducts = shouldUseV2
    ? products
    : products.slice(start, start + perPage);
  const totalItems = shouldUseV2
    ? Math.max(
        feedMeta.total,
        (currentPage - 1) * effectivePageSize + displayedProducts.length
      )
    : products.length;
  const headerCount = displayedProducts.length;

  if (loading) return <p className="loading">Loading products…</p>;

  return (
    <div className="productsPage">
      <div className="breadcrumb-container">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Products
            </li>
          </ol>
        </nav>
      </div>

      <div className="page-header">
        <h1 className="page-title">VANCOUVER</h1>
        <p className="page-description">
          Discover curated fashion and lifestyle products from Vancouver's
          finest retailers
        </p>
      </div>

      <div className="products-content">
        {!isMobile && (
          <FilterSidebar
            filters={filters}
            onFilterChange={onFilterChange}
            onResetFilters={resetFilters}
          />
        )}

        <div className="products-list-container">
          <div className="products-header">
            <h3 className="num-products">{headerCount} Products</h3>
          </div>

          <div className="number-sort-container">
            <div className="products-sort">
              {isMobile && (
                <button
                  className="filter-button"
                  onClick={() => setIsMobileFilterOpen(true)}
                >
                  Filter
                </button>
              )}
              <select
                className="form-control"
                value={sortOrder}
                onChange={onSort}
              >
                <option value="featured">Featured</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="price-asc">Price: Low to High</option>
              </select>
            </div>
          </div>

          <ProductList products={displayedProducts} />
        </div>
      </div>

      {isMobile && (
        <>
          <div
            className={`filter-overlay ${isMobileFilterOpen ? 'open' : ''}`}
            onClick={() => setIsMobileFilterOpen(false)}
          />
          <div
            className={`filter-sidebar-mobile ${
              isMobileFilterOpen ? 'open' : ''
            }`}
          >
            <div className="filter-sidebar-mobile__header">
              <h3 className="filter-sidebar-mobile__title">Filters</h3>
              <button
                type="button"
                className="filter-sidebar-mobile__close"
                onClick={() => setIsMobileFilterOpen(false)}
                aria-label="Close filters"
              >
                x
              </button>
            </div>
            <div className="filter-sidebar-mobile__content">
              <FilterSidebar
                filters={filters}
                onFilterChange={onFilterChange}
                onResetFilters={resetFilters}
              />
            </div>
          </div>
        </>
      )}

      <Pagination
        totalItems={totalItems}
        itemsPerPage={effectivePageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default ProductsPage;
